import { useEffect, useState, useMemo } from "react";
import { Download } from "lucide-react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { FORM_TYPE_LABEL } from "@/components/FormDetails";

const RANGES = [
  { key: "today", label: "Today", days: 1 },
  { key: "week", label: "This week", days: 7 },
  { key: "month", label: "This month", days: 30 },
  { key: "quarter", label: "Last 90 days", days: 90 },
];

export default function AnalyticsDashboard() {
  const [range, setRange] = useState("month");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const cfg = RANGES.find((r) => r.key === range);
    const end = new Date();
    const start = new Date(end.getTime() - cfg.days * 86400000);
    setLoading(true);
    api.get(`/analytics/summary?start=${start.toISOString()}&end=${end.toISOString()}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [range]);

  const totalByForm = useMemo(() => Object.entries(data?.by_form || {}), [data]);
  const donutMax = Math.max(1, ...totalByForm.map(([, v]) => v));

  const exportCsv = () => {
    if (!data) return;
    const lines = ["Metric,Value"];
    lines.push(`Total,${data.total}`);
    lines.push(`Today,${data.today}`);
    lines.push(`Week,${data.week}`);
    lines.push(`Month,${data.month}`);
    lines.push("");
    lines.push("By Form Type,Count");
    Object.entries(data.by_form || {}).forEach(([k, v]) => lines.push(`${k},${v}`));
    lines.push("");
    lines.push("By Department,Count");
    (data.by_dept || []).forEach(([k, v]) => lines.push(`${k},${v}`));
    lines.push("");
    lines.push("Top Requesters,Count");
    (data.by_applicant || []).forEach(([k, v]) => lines.push(`${k},${v}`));
    lines.push("");
    lines.push("Avg Stage (hours),");
    Object.entries(data.avg_stage_hours || {}).forEach(([k, v]) => lines.push(`${k},${v.toFixed(2)}`));

    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `vgws-analytics-${range}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const exportPdf = () => window.print(); // simple print-to-PDF path

  return (
    <Layout>
      <div className="mb-4 text-[11px] uppercase tracking-widest text-[#2d6b6b] font-semibold">Analytics</div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-serif text-3xl font-bold text-[#1f2320]" data-testid="analytics-heading">
          Requisition analytics
        </h1>
        <div className="flex items-center gap-2">
          <select value={range} onChange={(e) => setRange(e.target.value)}
                  className="input-vgws py-2 text-sm" data-testid="analytics-range">
            {RANGES.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
          </select>
          <button onClick={exportCsv} className="btn-vgws bg-[#2d6b6b] border-[#1c4a4a]" data-testid="export-csv">
            <Download size={14} /> CSV
          </button>
          <button onClick={exportPdf} className="btn-vgws" data-testid="export-pdf">
            <Download size={14} /> PDF
          </button>
        </div>
      </div>

      {loading && <div className="p-8 text-center text-[#6e7770]">Loading…</div>}
      {!loading && data && (
        <div className="mt-6 grid gap-4">
          <div className="grid sm:grid-cols-4 gap-4">
            <Stat label="In window" value={data.total} accent="maroon" testid="stat-total" />
            <Stat label="Today" value={data.today} accent="teal" testid="stat-today" />
            <Stat label="This week" value={data.week} accent="gold" testid="stat-week" />
            <Stat label="This month" value={data.month} accent="saffron" testid="stat-month" />
          </div>

          <div className="grid lg:grid-cols-2 gap-4">
            <div className="sheet p-5" data-testid="chart-by-form">
              <h3 className="font-serif font-semibold text-[#7a1f2b] mb-3">By form type</h3>
              <div className="space-y-2">
                {totalByForm.map(([k, v]) => (
                  <div key={k} className="flex items-center gap-3">
                    <div className="w-40 text-sm text-[#4a524c]">{FORM_TYPE_LABEL[k] || k}</div>
                    <div className="flex-1 h-3 rounded bg-[#f4e9eb] overflow-hidden">
                      <div className="h-3 bg-[#7a1f2b]" style={{ width: `${(v / donutMax) * 100}%` }} />
                    </div>
                    <div className="w-10 text-right text-sm font-semibold text-[#7a1f2b]">{v}</div>
                  </div>
                ))}
                {totalByForm.length === 0 && <div className="text-sm text-[#6e7770]">No data</div>}
              </div>
            </div>

            <div className="sheet p-5" data-testid="chart-avg-stage">
              <h3 className="font-serif font-semibold text-[#7a1f2b] mb-3">Avg time-to-approval (hours)</h3>
              <div className="space-y-2">
                {Object.entries(data.avg_stage_hours || {}).map(([k, v]) => (
                  <div key={k} className="flex items-center gap-3">
                    <div className="w-40 text-sm text-[#4a524c]">{k}</div>
                    <div className="flex-1 h-3 rounded bg-[#e8f2f2] overflow-hidden">
                      <div className="h-3 bg-[#2d6b6b]" style={{ width: `${Math.min(100, v * 5)}%` }} />
                    </div>
                    <div className="w-16 text-right text-sm font-semibold text-[#2d6b6b]">{v.toFixed(1)}h</div>
                  </div>
                ))}
                {Object.keys(data.avg_stage_hours || {}).length === 0 && (
                  <div className="text-sm text-[#6e7770]">Not enough data yet.</div>
                )}
              </div>
            </div>

            <div className="sheet p-5" data-testid="chart-tat-breach">
              <h3 className="font-serif font-semibold text-[#7a1f2b] mb-3">TAT breaches by HOD</h3>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[#6e7770] text-xs uppercase tracking-widest">
                    <th className="py-2">HOD</th><th className="text-right">Breaches</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.tat_breach_by_hod || []).map(([k, v]) => (
                    <tr key={k} className="border-t border-[#f2ecdf]">
                      <td className="py-2">{k}</td>
                      <td className="text-right font-semibold text-[#b3261e]">{v}</td>
                    </tr>
                  ))}
                  {(data.tat_breach_by_hod || []).length === 0 && (
                    <tr><td colSpan={2} className="text-[#6e7770] py-2">No breaches yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="sheet p-5" data-testid="chart-top-applicants">
              <h3 className="font-serif font-semibold text-[#7a1f2b] mb-3">Top 10 requesters</h3>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[#6e7770] text-xs uppercase tracking-widest">
                    <th className="py-2">Applicant</th><th className="text-right">Requisitions</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.by_applicant || []).map(([k, v]) => (
                    <tr key={k} className="border-t border-[#f2ecdf]">
                      <td className="py-2">{k}</td>
                      <td className="text-right font-semibold text-[#7a1f2b]">{v}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="sheet p-5" data-testid="chart-by-dept">
            <h3 className="font-serif font-semibold text-[#7a1f2b] mb-3">Top 10 departments</h3>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[#6e7770] text-xs uppercase tracking-widest">
                  <th className="py-2">Department</th><th className="text-right">Requisitions</th>
                </tr>
              </thead>
              <tbody>
                {(data.by_dept || []).map(([k, v]) => (
                  <tr key={k} className="border-t border-[#f2ecdf]">
                    <td className="py-2">{k}</td>
                    <td className="text-right font-semibold text-[#7a1f2b]">{v}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Layout>
  );
}

function Stat({ label, value, accent, testid }) {
  const tones = {
    maroon: "text-[#7a1f2b] border-[#7a1f2b]",
    teal: "text-[#2d6b6b] border-[#2d6b6b]",
    gold: "text-[#b48a45] border-[#b48a45]",
    saffron: "text-[#c8632a] border-[#c8632a]",
  };
  return (
    <div className={`sheet p-5 border-l-4 ${tones[accent]}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-[#6e7770] font-semibold">{label}</div>
      <div className={`font-serif text-3xl font-bold mt-1 ${tones[accent]}`}>{value}</div>
    </div>
  );
}
